import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, PlusCircle, Settings as SettingsIcon, LogOut, Menu, Shield, Building2, UserCheck, Bell, UserCog } from 'lucide-react';
import { AktaLogo } from './AktaLogo';
import { useApp } from '../context/AppContext';
import { ROLES } from '../mock';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';

const navByRole = {
  [ROLES.ADMIN]: [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/admin/team', label: 'Sales Heads & RMs', icon: Building2 },
    { to: '/admin/users', label: 'User Management', icon: UserCog },
    { to: '/clients', label: 'All Clients', icon: Users },
    { to: '/alerts', label: 'RM Coaching', icon: Bell },
    { to: '/settings', label: 'Settings', icon: SettingsIcon }
  ],
  [ROLES.SALES_HEAD]: [
    { to: '/sales', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/sales/rms', label: 'My RMs', icon: UserCheck },
    { to: '/clients', label: 'Clients', icon: Users },
    { to: '/alerts', label: 'RM Coaching', icon: Bell },
    { to: '/settings', label: 'Settings', icon: SettingsIcon }
  ],
  [ROLES.RM]: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/clients', label: 'Clients', icon: Users },
    { to: '/clients/new', label: 'New Client', icon: PlusCircle },
    { to: '/settings', label: 'Settings', icon: SettingsIcon }
  ]
};

const roleBadge = (role) => {
  if (role === ROLES.ADMIN) return { label: 'Admin', className: 'bg-brand-indigo text-white', icon: Shield };
  if (role === ROLES.SALES_HEAD) return { label: 'Sales Head', className: 'bg-brand-cyan text-white', icon: Building2 };
  return { label: 'Relationship Mgr', className: 'bg-brand-green text-white', icon: UserCheck };
};

const SidebarInner = ({ onNavigate }) => {
  const { user, logout } = useApp();
  const nav = useNavigate();
  const items = navByRole[user?.role] || [];
  const initials = (user?.name || 'AW').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const badge = roleBadge(user?.role);
  const BadgeIcon = badge.icon;

  return (
    <div className="h-full flex flex-col bg-white border-r border-brand-border">
      <div className="px-5 py-5 border-b border-brand-border">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-3">
          <AktaLogo size={42} showText={false} />
          <div className="leading-tight">
            <div className="font-bold text-brand-indigo text-lg">Akta Wealth</div>
            <div className="text-[10px] text-muted-foreground tracking-wider">FINANCIAL MRI · ARN-47282</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-indigo text-white' : 'text-brand-indigo/80 hover:bg-brand-bg hover:text-brand-indigo'
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-brand-border">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-brand-bg">
          <Avatar className="h-9 w-9">
            {user?.picture ? <AvatarImage src={user.picture} /> : null}
            <AvatarFallback className="bg-brand-indigo text-white text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-brand-indigo truncate">{user?.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button data-testid="logout-button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => { logout(); nav('/login'); onNavigate && onNavigate(); }}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${badge.className}`}>
          <BadgeIcon className="h-3 w-3" /> {badge.label}
        </div>
      </div>
    </div>
  );
};

export const Footer = () => (
  <footer className="border-t border-brand-border bg-white/70 px-4 md:px-8 py-3 text-[11px] text-brand-indigo/70 flex flex-wrap items-center justify-between gap-2">
    <div>AMFI-registered Mutual Fund Distributor · ARN-47282 · Not a SEBI Registered Investment Adviser</div>
    <div>Mutual Fund investments are subject to market risks.</div>
  </footer>
);

export const AppShell = ({ children }) => {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-brand-bg">
      <aside className="hidden lg:flex w-64 shrink-0 sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-brand-border sticky top-0 z-30">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarInner onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <AktaLogo size={28} showText={false} />
            <div className="font-semibold text-brand-indigo">Akta Wealth</div>
          </div>
          <div className="w-9" />
        </div>

        <main className="flex-1 min-w-0">{children}</main>
        <Footer />
      </div>
    </div>
  );
};

export default AppShell;
